import {
  Activity,
  Banknote,
  CalendarClock,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  CircleCheckBig,
  CircleAlert,
  Clock3,
  CreditCard,
  Gift,
  History,
  Hash,
  MapPin,
  Minus,
  Plus,
  PencilLine,
  ReceiptText,
  RefreshCw,
  RotateCcw,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  Users,
  WalletCards,
  X,
  Zap,
} from "lucide-react";
import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { api } from "../api";
import { WechatPaymentOrders } from "../components/wechat-payment-orders";
import {
  ActionButton,
  EmptyState,
  ErrorState,
  LoadingState,
  Pagination,
  Tag,
} from "../components/design-system";
import { AutoDormCheckGrantDialog } from "../components/auto-dorm-check-grant-dialog";
import { AutoDormCheckPlanEditor } from "../components/auto-dorm-check-plan-editor";
import { FilterPopover } from "../components/filter-popover";
import { ConfirmDialog } from "../components/ui";
import { useDrawerMotion } from "../hooks/use-drawer-motion";
import { useResource } from "../hooks/use-resource";
import {
  isWithinSchoolCheckInWindow,
  useSchoolCheckInWindow,
  type SchoolCheckInWindow,
} from "../hooks/use-school-check-in-window";
import type {
  AutoDormCheckIn,
  AutoDormCheckPlan,
  AutoDormCheckQuotaActivity,
  AutoDormCheckQuotaBalance,
  AutoDormCheckQuotaCredit,
  AutoDormCheckQuotaLedgerItem,
  AutoDormCheckQuotaLedgerPeriodMode,
  AutoDormCheckQuotaLedgerResult,
  AutoDormCheckQuotaLedgerType,
  AutoDormCheckQuotaUser,
  AutoDormCheckQuotaUserDetail,
  AutoDormCheckTask,
  AutoDormCheckTaskStatus,
  AutoDormCheckSettings,
  AutoDormCheckUser,
  AutoDormCheckUserFilter,
  AutoDormCheckUserResult,
  Paginated,
} from "../types";
import { formatDateTime } from "../utils";

const USER_PAGE_SIZE = 8;
const DETAIL_PAGE_SIZE = 6;
const TASK_TIME_FORMATTERS = new Map<string, Intl.DateTimeFormat>();

const TASK_STATUS: Record<
  AutoDormCheckTaskStatus,
  { label: string; tone: "neutral" | "clay" | "sage" | "danger" }
> = {
  pending: { label: "等待执行", tone: "neutral" },
  running: { label: "执行中", tone: "clay" },
  succeeded: { label: "已完成", tone: "sage" },
  failed: { label: "失败", tone: "danger" },
  skipped: { label: "已跳过", tone: "neutral" },
  cancelled: { label: "已取消", tone: "neutral" },
};

type TaskStatusPresentation = (typeof TASK_STATUS)[AutoDormCheckTaskStatus];

export function hasAutoDormCheckQuota(
  balance: AutoDormCheckQuotaBalance,
): boolean {
  return (
    balance.remainingSeconds > 0 ||
    balance.remainingUses > 0 ||
    (balance.reservedUses || 0) > 0
  );
}

export function autoDormCheckTaskPresentation(
  task: Pick<AutoDormCheckTask, "status" | "executionOrigin">,
  paymentEnabled: boolean | null,
  balance: AutoDormCheckQuotaBalance,
): TaskStatusPresentation {
  if (
    paymentEnabled === true &&
    task.executionOrigin === "automatic" &&
    task.status === "pending" &&
    !hasAutoDormCheckQuota(balance)
  ) {
    return { label: "将跳过", tone: "neutral" };
  }
  return TASK_STATUS[task.status];
}

function taskTimeFormatter(timeZone: string): Intl.DateTimeFormat {
  const cached = TASK_TIME_FORMATTERS.get(timeZone);
  if (cached) return cached;
  const formatter = new Intl.DateTimeFormat("zh-CN", {
    timeZone,
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  });
  TASK_TIME_FORMATTERS.set(timeZone, formatter);
  return formatter;
}

function formatTaskTime(task: AutoDormCheckTask): string {
  if (!task.scheduledAt) return "—";
  const date = new Date(task.scheduledAt);
  if (Number.isNaN(date.getTime())) return "—";
  try {
    return taskTimeFormatter(task.sourceTimezone || "Asia/Shanghai").format(
      date,
    );
  } catch {
    return formatDateTime(task.scheduledAt);
  }
}

export function compactLocationName(
  locationName: string,
  sideLength = 5,
): string {
  const characters = Array.from(locationName.trim());
  const safeSideLength = Math.max(1, Math.floor(sideLength));
  if (characters.length <= safeSideLength * 2 + 1) return characters.join("");
  return `${characters.slice(0, safeSideLength).join("")}…${characters
    .slice(-safeSideLength)
    .join("")}`;
}

export function isImmediateCheckInDisabled(
  user: Pick<
    AutoDormCheckUser,
    "accountActive" | "credentialStatus" | "todayTaskStatus" | "checkedInToday"
  >,
  schoolWindowOpen: boolean,
): boolean {
  return (
    !schoolWindowOpen ||
    !user.accountActive ||
    user.checkedInToday ||
    user.todayTaskStatus === "succeeded" ||
    user.todayTaskStatus === "running"
  );
}

export function AutoDormCheckPage() {
  const [page, setPage] = useState(1);
  const [userFilter, setUserFilter] = useState<AutoDormCheckUserFilter>("all");
  const [userQueryDraft, setUserQueryDraft] = useState("");
  const [userQuery, setUserQuery] = useState("");
  const [activeWorkspace, setActiveWorkspace] = useState<
    "runtime" | "orders" | "accounts" | "ledger"
  >("runtime");
  const [savingSetting, setSavingSetting] = useState(false);
  const [paymentConfirmation, setPaymentConfirmation] = useState<
    boolean | null
  >(null);
  const [grantDialogOpen, setGrantDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<AutoDormCheckPlan | null>(
    null,
  );
  const [quotaPage, setQuotaPage] = useState(1);
  const [quotaQueryDraft, setQuotaQueryDraft] = useState("");
  const [quotaQuery, setQuotaQuery] = useState("");
  const [ledgerPage, setLedgerPage] = useState(1);
  const [ledgerMode, setLedgerMode] =
    useState<AutoDormCheckQuotaLedgerPeriodMode>("month");
  const [ledgerPeriod, setLedgerPeriod] = useState("");
  const [ledgerType, setLedgerType] =
    useState<AutoDormCheckQuotaLedgerType>("all");
  const [ledgerQueryDraft, setLedgerQueryDraft] = useState("");
  const [ledgerQuery, setLedgerQuery] = useState("");
  const [selectedQuotaUserId, setSelectedQuotaUserId] = useState<string | null>(
    null,
  );
  const settings = useResource(() => api.autoDormCheckSettings(), []);
  const plans = useResource(() => api.autoDormCheckPlans(), []);
  const users = useResource(
    () =>
      api.autoDormCheckUsers({
        page,
        pageSize: USER_PAGE_SIZE,
        q: userQuery || undefined,
        filter: userFilter,
      }),
    [page, userFilter, userQuery],
  );
  const quotaUsers = useResource(
    () =>
      api.autoDormCheckQuotaUsers({
        page: quotaPage,
        pageSize: 12,
        q: quotaQuery || undefined,
      }),
    [quotaPage, quotaQuery],
  );
  const quotaLedger = useResource(
    () =>
      api.autoDormCheckQuotaLedger({
        page: ledgerPage,
        pageSize: 15,
        q: ledgerQuery || undefined,
        mode: ledgerMode,
        period: ledgerPeriod || undefined,
        type: ledgerType,
      }),
    [ledgerMode, ledgerPage, ledgerPeriod, ledgerQuery, ledgerType],
  );

  const updateGlobalSetting = async (
    key: "entryEnabled" | "functionEnabled" | "paymentEnabled",
    enabled: boolean,
  ): Promise<boolean> => {
    if (savingSetting) return false;
    setSavingSetting(true);
    try {
      const next = await api.updateAutoDormCheckSettings({ [key]: enabled });
      settings.setData(next);
      const target =
        key === "entryEnabled"
          ? "入口"
          : key === "functionEnabled"
            ? "功能"
            : "付费";
      toast.success(`${target}已${enabled ? "开启" : "关闭"}`);
      return true;
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "设置保存失败");
      return false;
    } finally {
      setSavingSetting(false);
    }
  };

  const refreshAll = () =>
    Promise.all([
      settings.refresh(),
      plans.refresh(),
      users.refresh(),
      quotaUsers.refresh(),
      quotaLedger.refresh(),
    ]).then(() => undefined);

  const workspaceTabs = [
    {
      key: "runtime" as const,
      label: "用户与任务",
      description: "运行控制与打卡执行",
    },
    {
      key: "orders" as const,
      label: "订单",
      description: "支付与退款",
    },
    {
      key: "accounts" as const,
      label: "额度账户",
      description: "余额、充值与使用明细",
    },
    {
      key: "ledger" as const,
      label: "额度明细",
      description: "购买、发放、修改与撤回记录",
    },
  ];

  return (
    <div className="auto-dorm-check-page">
      <header className="adc-page-header rise-in">
        <div className="min-w-0">
          <p className="adc-kicker">AUTOMATIC DORM CHECK</p>
          <h1>自动查寝管理</h1>
          <p className="adc-page-description">运行规则、打卡任务与用户额度</p>
        </div>
        <div className="adc-page-actions">
          {settings.data ? (
            <span
              className={`adc-status-pill ${settings.data.paymentEnabled ? "is-clay" : "is-positive"}`}
            >
              <i />
              {settings.data.paymentEnabled ? "付费模式" : "免费模式"}
            </span>
          ) : null}
          <button
            type="button"
            className="adc-button adc-button-clay"
            onClick={() => setGrantDialogOpen(true)}
          >
            <Gift size={14} />
            发放额度
          </button>
          <button
            type="button"
            className="adc-button adc-button-outline"
            disabled={
              settings.refreshing ||
              plans.refreshing ||
              users.refreshing ||
              quotaUsers.refreshing ||
              quotaLedger.refreshing
            }
            onClick={() => void refreshAll()}
          >
            <RefreshCw
              size={14}
              className={
                settings.refreshing ||
                plans.refreshing ||
                users.refreshing ||
                quotaUsers.refreshing ||
                quotaLedger.refreshing
                  ? "animate-spin"
                  : ""
              }
            />
            刷新
          </button>
        </div>
      </header>

      <nav
        className="adc-workspace-tabs rise-in rise-in-1"
        aria-label="自动查寝管理分区"
      >
        {workspaceTabs.map((tab) => {
          const selected = activeWorkspace === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              aria-current={selected ? "page" : undefined}
              onClick={() => setActiveWorkspace(tab.key)}
              title={tab.description}
              className={`adc-workspace-tab ${selected ? "is-active" : ""}`}
            >
              {tab.label}
              {tab.key === "accounts" && quotaUsers.data ? (
                <span>
                  {settings.data?.paymentEnabled
                    ? quotaUsers.data.pagination.total
                    : 0}
                </span>
              ) : null}
            </button>
          );
        })}
      </nav>

      {activeWorkspace === "runtime" ? (
        <div className="adc-workspace rise-in rise-in-2">
          {settings.error ? (
            <ErrorState
              error={settings.error}
              onRetry={() => void settings.refresh()}
            />
          ) : null}
          <section className="adc-card adc-runtime-panel">
            <div className="adc-card-toolbar">
              <div className="adc-toolbar-title">
                <span className="adc-toolbar-icon">
                  <ShieldCheck size={16} />
                </span>
                <div>
                  <h2>运行控制</h2>
                  <p>全局开关与打卡时段</p>
                </div>
              </div>
              <span className="adc-muted-time">
                更新于 {formatDateTime(settings.data?.updatedAt)}
              </span>
            </div>
            {settings.loading && !settings.data ? (
              <LoadingState rows={4} />
            ) : (
              <div className="adc-control-grid">
                <GlobalSwitchRow
                  title="小程序入口"
                  description="学生端入口与任务总开关"
                  enabled={Boolean(settings.data?.entryEnabled)}
                  disabled={!settings.data || savingSetting}
                  onChange={(enabled) =>
                    void updateGlobalSetting("entryEnabled", enabled)
                  }
                />
                <GlobalSwitchRow
                  title="自动查寝"
                  description="所有用户的自动打卡"
                  enabled={Boolean(settings.data?.functionEnabled)}
                  disabled={
                    !settings.data ||
                    savingSetting ||
                    !settings.data.entryEnabled
                  }
                  onChange={(enabled) =>
                    void updateGlobalSetting("functionEnabled", enabled)
                  }
                />
                <GlobalSwitchRow
                  title="打卡付费"
                  description="关闭后免费且暂停额度消耗"
                  enabled={Boolean(settings.data?.paymentEnabled)}
                  disabled={!settings.data || savingSetting}
                  icon={<CreditCard size={16} />}
                  onChange={setPaymentConfirmation}
                />
                <CheckInWindowsEditor
                  settings={settings.data}
                  onSaved={settings.setData}
                />
              </div>
            )}
          </section>

          <PaymentPlansSection
            plans={plans.data?.items || []}
            loading={plans.loading && !plans.data}
            refreshing={plans.refreshing}
            error={plans.error}
            onRetry={plans.refresh}
            onEdit={setEditingPlan}
          />

          <EnabledUsersSection
            result={users.data}
            schoolWindow={settings.data}
            plans={plans.data?.items || []}
            paymentEnabled={settings.data?.paymentEnabled ?? null}
            query={userQueryDraft}
            filter={userFilter}
            loading={users.loading && !users.data}
            error={users.error}
            onRetry={users.refresh}
            onPageChange={setPage}
            onQueryChange={setUserQueryDraft}
            onSearch={() => {
              setPage(1);
              setUserQuery(userQueryDraft.trim());
            }}
            onFilterChange={(nextFilter) => {
              setPage(1);
              setUserFilter(nextFilter);
            }}
            onChanged={async () => {
              await Promise.all([
                users.refresh(),
                quotaUsers.refresh(),
                quotaLedger.refresh(),
              ]);
            }}
          />
        </div>
      ) : null}

      {activeWorkspace === "orders" ? (
        <div className="adc-workspace rise-in rise-in-2">
          <WechatPaymentOrders
            onRefunded={() => {
              void Promise.all([
                quotaUsers.refresh(),
                quotaLedger.refresh(),
                users.refresh(),
              ]);
            }}
          />
        </div>
      ) : null}

      {activeWorkspace === "accounts" ? (
        <QuotaAccountsSection
          query={quotaQueryDraft}
          onQueryChange={setQuotaQueryDraft}
          onSearch={() => {
            setQuotaPage(1);
            setQuotaQuery(quotaQueryDraft.trim());
          }}
          result={quotaUsers.data}
          loading={quotaUsers.loading && !quotaUsers.data}
          error={quotaUsers.error}
          onRetry={quotaUsers.refresh}
          onPageChange={setQuotaPage}
          onSelect={setSelectedQuotaUserId}
          onChanged={async () => {
            await Promise.all([quotaUsers.refresh(), quotaLedger.refresh()]);
          }}
          paymentEnabled={settings.data?.paymentEnabled ?? null}
        />
      ) : null}

      {activeWorkspace === "ledger" ? (
        <QuotaLedgerSection
          result={quotaLedger.data}
          loading={quotaLedger.loading && !quotaLedger.data}
          error={quotaLedger.error}
          onRetry={quotaLedger.refresh}
          onPageChange={setLedgerPage}
          mode={ledgerMode}
          onModeChange={(mode) => {
            setLedgerPage(1);
            setLedgerMode(mode);
            setLedgerPeriod("");
          }}
          period={ledgerPeriod}
          onPeriodChange={(period) => {
            setLedgerPage(1);
            setLedgerPeriod(period);
          }}
          type={ledgerType}
          onTypeChange={(type) => {
            setLedgerPage(1);
            setLedgerType(type);
          }}
          query={ledgerQueryDraft}
          onQueryChange={setLedgerQueryDraft}
          onSearch={() => {
            setLedgerPage(1);
            setLedgerQuery(ledgerQueryDraft.trim());
          }}
        />
      ) : null}

      <ConfirmDialog
        open={paymentConfirmation !== null}
        title={paymentConfirmation ? "开启打卡付费？" : "关闭打卡付费？"}
        description={
          paymentConfirmation
            ? "开启后会恢复已购买额度的消耗；没有可用额度的用户需要先购买套餐。"
            : "关闭后所有用户可免费打卡；已购买的次数和剩余时长会暂停并保留，重新开启后继续使用。"
        }
        confirmLabel={paymentConfirmation ? "确认开启" : "确认关闭"}
        busy={savingSetting}
        onCancel={() => setPaymentConfirmation(null)}
        onConfirm={() => {
          if (paymentConfirmation === null) return;
          void updateGlobalSetting("paymentEnabled", paymentConfirmation).then(
            (saved) => {
              if (saved) setPaymentConfirmation(null);
            },
          );
        }}
      />

      <AutoDormCheckGrantDialog
        open={grantDialogOpen}
        plans={plans.data?.items || []}
        onClose={() => setGrantDialogOpen(false)}
        onGranted={async () => {
          await Promise.all([
            users.refresh(),
            quotaUsers.refresh(),
            quotaLedger.refresh(),
          ]);
        }}
      />
      {editingPlan ? (
        <AutoDormCheckPlanEditor
          plan={editingPlan}
          onClose={() => setEditingPlan(null)}
          onSaved={(saved) => {
            plans.setData((current) =>
              current
                ? {
                    items: current.items.map((item) =>
                      item.id === saved.id ? saved : item,
                    ),
                  }
                : { items: [saved] },
            );
          }}
          onStale={async () => {
            await plans.refresh();
            setEditingPlan(null);
          }}
        />
      ) : null}
      <QuotaUserDetailDialog
        userId={selectedQuotaUserId}
        onClose={() => setSelectedQuotaUserId(null)}
        onChanged={async () => {
          await Promise.all([
            users.refresh(),
            quotaUsers.refresh(),
            quotaLedger.refresh(),
          ]);
        }}
      />
    </div>
  );
}

function PaymentPlansSection({
  plans,
  loading,
  refreshing,
  error,
  onRetry,
  onEdit,
}: {
  plans: AutoDormCheckPlan[];
  loading: boolean;
  refreshing: boolean;
  error: Error | null;
  onRetry: () => Promise<unknown>;
  onEdit: (plan: AutoDormCheckPlan) => void;
}) {
  return (
    <section className="adc-card overflow-hidden">
      <div className="adc-card-toolbar">
        <div className="adc-toolbar-title">
          <span className="adc-toolbar-icon">
            <CreditCard size={16} />
          </span>
          <div>
            <h2>打卡套餐</h2>
            <p>学生购买页展示的价格、额度与说明</p>
          </div>
        </div>
        <button
          type="button"
          className="adc-button adc-button-outline"
          disabled={refreshing}
          onClick={() => void onRetry()}
        >
          <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} />
          刷新套餐
        </button>
      </div>

      {error && !plans.length ? (
        <ErrorState error={error} onRetry={() => void onRetry()} />
      ) : loading ? (
        <LoadingState rows={3} />
      ) : plans.length ? (
        <div className="grid gap-3 border-t border-sand-light/65 p-4 sm:grid-cols-2 xl:grid-cols-3 xl:p-5">
          {plans.map((plan) => {
            const Icon = plan.billingType === "time" ? Clock3 : Hash;
            return (
              <article
                key={plan.id}
                className="group flex min-h-48 min-w-0 flex-col rounded-2xl border border-sand-light bg-paper px-4 py-4 transition hover:-translate-y-0.5 hover:border-sand hover:shadow-[0_12px_30px_rgba(69,58,48,0.08)]"
              >
                <div className="flex min-w-0 items-start gap-3">
                  <span
                    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${
                      plan.billingType === "time"
                        ? "bg-clay-soft text-clay-deep"
                        : "bg-[#E9F0E5] text-sage"
                    }`}
                  >
                    <Icon size={16} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex min-w-0 items-center gap-2">
                      <h3 className="truncate text-[13.5px] font-semibold text-ink">
                        {plan.name}
                      </h3>
                      <Tag tone={plan.billingType === "time" ? "clay" : "sage"}>
                        {plan.billingType === "time" ? "按时间" : "按次数"}
                      </Tag>
                    </div>
                    <p className="mt-0.5 truncate font-mono text-[9.5px] text-ink-faint">
                      {plan.id}
                    </p>
                  </div>
                  <strong className="shrink-0 font-display text-[20px] font-medium tracking-tight text-ink">
                    {formatMoney(plan.priceCents)}
                  </strong>
                </div>

                <div className="mt-4 flex items-center gap-2">
                  <span className="rounded-lg border border-sand-light bg-cream/55 px-2.5 py-1.5 text-[11px] font-medium text-ink-soft">
                    {plan.quotaLabel}
                  </span>
                </div>
                <p className="mt-3 min-h-10 flex-1 text-[11.5px] leading-relaxed text-ink-soft">
                  {plan.description || "暂无说明"}
                </p>
                <div className="mt-3 flex items-center justify-end border-t border-sand-light/60 pt-3">
                  <button
                    type="button"
                    className="adc-button adc-button-outline"
                    onClick={() => onEdit(plan)}
                  >
                    <PencilLine size={13} />
                    编辑
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <EmptyState title="暂无套餐" description="请刷新后重试。" />
      )}
    </section>
  );
}

function EnabledUsersSection({
  schoolWindow,
  result,
  plans,
  paymentEnabled,
  query,
  filter,
  loading,
  error,
  onRetry,
  onPageChange,
  onQueryChange,
  onSearch,
  onFilterChange,
  onChanged,
}: {
  result: AutoDormCheckUserResult | null;
  schoolWindow: SchoolCheckInWindow | null;
  plans: AutoDormCheckPlan[];
  paymentEnabled: boolean | null;
  query: string;
  filter: AutoDormCheckUserFilter;
  loading: boolean;
  error: Error | null;
  onRetry: () => Promise<unknown>;
  onPageChange: (page: number) => void;
  onQueryChange: (value: string) => void;
  onSearch: () => void;
  onFilterChange: (filter: AutoDormCheckUserFilter) => void;
  onChanged: () => Promise<unknown>;
}) {
  const visibleUsers = result?.items || [];
  const schoolWindowOpen = useSchoolCheckInWindow(schoolWindow);
  const planLabels = new Map(plans.map((plan) => [plan.code, plan.label]));
  const completed = visibleUsers.filter(
    (user) => user.checkedInToday || user.todayTaskStatus === "succeeded",
  ).length;
  const pending = visibleUsers.filter(
    (user) =>
      !user.checkedInToday &&
      !(paymentEnabled === true && !hasAutoDormCheckQuota(user.quotaBalance)) &&
      (user.todayTaskStatus === "pending" ||
        user.todayTaskStatus === "running" ||
        user.todayTaskStatus === null),
  ).length;
  const filters: Array<{
    value: AutoDormCheckUserFilter;
    label: string;
    count: number | null;
  }> = [
    { value: "all", label: "全部", count: result?.facets.all ?? null },
    { value: "paid", label: "付费用户", count: result?.facets.paid ?? null },
    {
      value: "succeeded",
      label: "已完成",
      count: result?.facets.succeeded ?? null,
    },
    {
      value: "skipped",
      label: "已跳过",
      count: result?.facets.skipped ?? null,
    },
    {
      value: "pending",
      label: "待执行",
      count: result?.facets.pending ?? null,
    },
    {
      value: "attention",
      label: "需关注",
      count: result?.facets.attention ?? null,
    },
  ];

  return (
    <section className="adc-card overflow-hidden">
      <div className="adc-card-toolbar adc-users-toolbar">
        <div className="adc-users-toolbar-main">
          <div className="adc-toolbar-title">
            <span className="adc-toolbar-icon">
              <Users size={16} />
            </span>
            <div>
              <div className="flex items-baseline gap-2">
                <h2>已启用用户</h2>
                <span className="adc-count">
                  {result ? result.pagination.total : "—"}
                </span>
              </div>
              <p>任务状态、打卡额度与人工打卡</p>
            </div>
          </div>
          <div
            className="adc-user-filters"
            role="tablist"
            aria-label="用户筛选"
          >
            {filters.map((option) => (
              <button
                key={option.value}
                type="button"
                role="tab"
                aria-selected={filter === option.value}
                className={filter === option.value ? "is-active" : ""}
                onClick={() => onFilterChange(option.value)}
              >
                {option.label}
                {option.count !== null ? <span>{option.count}</span> : null}
              </button>
            ))}
          </div>
        </div>
        <AutoDormSearch
          value={query}
          onChange={onQueryChange}
          onSubmit={onSearch}
          placeholder="搜索姓名、学号或班级…"
        />
      </div>

      <div className="adc-metric-strip">
        <span>
          付费用户 <b>{result?.facets.paid ?? "—"}</b>
        </span>
        <span className="is-positive">
          本页完成 <b>{completed}</b>
        </span>
        <span className="is-warning">
          本页待执行 <b>{pending}</b>
        </span>
        <span
          className={(result?.facets.attention || 0) > 0 ? "is-danger" : ""}
        >
          {(result?.facets.attention || 0) > 0 ? (
            <CircleAlert size={13} />
          ) : null}
          今日失败 <b>{result?.facets.attention ?? "—"}</b>
        </span>
      </div>

      {error ? (
        <div className="p-4">
          <ErrorState error={error} onRetry={() => void onRetry()} />
        </div>
      ) : loading ? (
        <LoadingState rows={8} />
      ) : visibleUsers.length ? (
        <>
          <div className="adc-user-table-scroll">
            <div className="adc-user-grid adc-user-table-head" aria-hidden>
              <span>用户</span>
              <span>开启时间</span>
              <span>打卡额度</span>
              <span>上次打卡位置</span>
              <span>最近任务 · 近三次</span>
              <span className="text-right">操作</span>
            </div>
            <div className="adc-user-table-body">
              {visibleUsers.map((user) => (
                <StudentRow
                  key={user.userId}
                  user={user}
                  schoolWindow={schoolWindow}
                  schoolWindowOpen={schoolWindowOpen}
                  paymentEnabled={paymentEnabled}
                  planLabel={
                    user.latestPaidPlanCode
                      ? planLabels.get(user.latestPaidPlanCode) || "付费用户"
                      : null
                  }
                  onChanged={onChanged}
                />
              ))}
            </div>
          </div>
          <Pagination
            page={result!.pagination.page}
            pages={result!.pagination.totalPages}
            total={result!.pagination.total}
            onChange={onPageChange}
            className="adc-card-footer"
          />
        </>
      ) : (
        <EmptyState
          title="没有匹配的用户"
          description={
            query.trim() || filter !== "all"
              ? "请调整搜索或筛选条件。"
              : "学生开启自动查寝后会显示在这里。"
          }
        />
      )}
    </section>
  );
}

function AutoDormSearch({
  value,
  onChange,
  onSubmit,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  placeholder: string;
}) {
  return (
    <form
      className="adc-search"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <Search size={14} aria-hidden />
      <input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        aria-label={placeholder.replace("…", "")}
      />
    </form>
  );
}

const LEDGER_TYPE_OPTIONS: Array<{
  value: AutoDormCheckQuotaLedgerType;
  label: string;
}> = [
  { value: "all", label: "全部行为" },
  { value: "purchase", label: "用户购买" },
  { value: "grant", label: "管理员发放" },
  { value: "adjustment", label: "管理员修改" },
  { value: "revocation", label: "管理员撤回" },
];

function QuotaLedgerSection({
  result,
  loading,
  error,
  onRetry,
  onPageChange,
  mode,
  onModeChange,
  period,
  onPeriodChange,
  type,
  onTypeChange,
  query,
  onQueryChange,
  onSearch,
}: {
  result: AutoDormCheckQuotaLedgerResult | null;
  loading: boolean;
  error: Error | null;
  onRetry: () => Promise<unknown>;
  onPageChange: (page: number) => void;
  mode: AutoDormCheckQuotaLedgerPeriodMode;
  onModeChange: (mode: AutoDormCheckQuotaLedgerPeriodMode) => void;
  period: string;
  onPeriodChange: (period: string) => void;
  type: AutoDormCheckQuotaLedgerType;
  onTypeChange: (type: AutoDormCheckQuotaLedgerType) => void;
  query: string;
  onQueryChange: (value: string) => void;
  onSearch: () => void;
}) {
  const periods =
    mode === "month"
      ? result?.periodOptions.months || []
      : result?.periodOptions.semesters || [];
  const selectedPeriod = period || result?.period.key || "";
  const administratorActions = result
    ? result.summary.grantCount +
      result.summary.adjustmentCount +
      result.summary.revocationCount
    : 0;

  return (
    <section className="adc-workspace rise-in rise-in-2">
      {error ? (
        <ErrorState error={error} onRetry={() => void onRetry()} />
      ) : null}

      <div className="adc-ledger-summary-grid">
        <section className="adc-ledger-revenue-card">
          <div className="adc-ledger-summary-label">
            <span>
              <Banknote size={15} />
            </span>
            {result?.period.label || "所选周期"}收入
          </div>
          <strong>{formatMoney(result?.summary.revenueCents || 0)}</strong>
          <p>
            {result?.period.startDate || "—"} 至 {result?.period.endDate || "—"}
          </p>
          <div className="adc-ledger-plan-breakdown">
            {result?.summary.plans.length ? (
              result.summary.plans.map((plan) => (
                <span key={plan.planCode}>
                  {plan.planLabel}
                  <b>{formatMoney(plan.revenueCents)}</b>
                </span>
              ))
            ) : (
              <span>本周期暂无购买收入</span>
            )}
          </div>
        </section>

        <LedgerMetricCard
          label="成功订单"
          value={String(result?.summary.orderCount || 0)}
          detail="仅统计支付成功订单"
          icon={<ReceiptText size={15} />}
        />
        <LedgerMetricCard
          label="付费用户"
          value={String(result?.summary.payingUserCount || 0)}
          detail="所选周期内去重"
          icon={<Users size={15} />}
        />
        <LedgerMetricCard
          label="管理员变更"
          value={String(administratorActions)}
          detail={`${result?.summary.grantCount || 0} 笔发放 · ${result?.summary.adjustmentCount || 0} 笔修改 · ${result?.summary.revocationCount || 0} 笔撤回`}
          icon={<SlidersHorizontal size={15} />}
        />
      </div>

      <section className="adc-card overflow-visible">
        <div className="adc-ledger-toolbar">
          <div className="adc-toolbar-title">
            <span className="adc-toolbar-icon">
              <ReceiptText size={16} />
            </span>
            <div>
              <div className="flex items-baseline gap-2">
                <h2>额度明细</h2>
                <span className="adc-count">
                  {result?.pagination.total ?? "—"}
                </span>
              </div>
              <p>购买、发放、修改与撤回记录</p>
            </div>
          </div>
          <AutoDormSearch
            value={query}
            onChange={onQueryChange}
            onSubmit={onSearch}
            placeholder="搜索姓名或学号…"
          />
        </div>

        <div className="adc-ledger-filters">
          <div
            className="adc-period-mode"
            role="radiogroup"
            aria-label="统计周期"
          >
            <button
              type="button"
              role="radio"
              aria-checked={mode === "month"}
              className={mode === "month" ? "is-active" : ""}
              onClick={() => onModeChange("month")}
            >
              按月
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={mode === "semester"}
              className={mode === "semester" ? "is-active" : ""}
              disabled={!result || result.periodOptions.semesters.length === 0}
              onClick={() => onModeChange("semester")}
            >
              按学期
            </button>
          </div>
          <div className="adc-ledger-filter-menus">
            <FilterPopover
              label="周期"
              hideLabel
              value={selectedPeriod}
              options={periods.map((item) => ({
                value: item.key,
                label: item.label,
              }))}
              onChange={onPeriodChange}
              className="adc-period-filter"
              triggerClassName="w-full justify-between rounded-xl"
            />
            <FilterPopover
              label="行为"
              hideLabel
              value={type}
              options={LEDGER_TYPE_OPTIONS}
              onChange={onTypeChange}
              triggerClassName="min-w-36 justify-between rounded-xl"
            />
          </div>
        </div>

        {loading ? (
          <LoadingState rows={8} />
        ) : result?.items.length ? (
          <>
            <div className="adc-ledger-mobile-list md:hidden">
              {result.items.map((item) => (
                <LedgerMobileCard key={item.id} item={item} />
              ))}
            </div>
            <div className="hidden overflow-x-auto md:block">
              <table className="adc-table adc-ledger-table min-w-[980px]">
                <thead>
                  <tr>
                    <th className="w-[155px]">时间</th>
                    <th className="w-[190px]">用户</th>
                    <th className="w-[135px]">行为</th>
                    <th className="w-[135px]">额度变动</th>
                    <th className="w-[105px]">收入</th>
                    <th>经办与说明</th>
                  </tr>
                </thead>
                <tbody>
                  {result.items.map((item) => (
                    <LedgerTableRow key={item.id} item={item} />
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              page={result.pagination.page}
              pages={result.pagination.totalPages}
              total={result.pagination.total}
              onChange={onPageChange}
              className="adc-card-footer"
            />
          </>
        ) : (
          <EmptyState
            title="没有额度明细"
            description={
              query.trim()
                ? "所选周期内没有匹配的用户记录。"
                : "所选周期内没有符合筛选条件的额度行为。"
            }
          />
        )}
      </section>
    </section>
  );
}

function LedgerMetricCard({
  label,
  value,
  detail,
  icon,
}: {
  label: string;
  value: string;
  detail: string;
  icon: ReactNode;
}) {
  return (
    <section className="adc-ledger-metric-card">
      <div className="adc-ledger-summary-label">
        <span>{icon}</span>
        {label}
      </div>
      <strong>{value}</strong>
      <p>{detail}</p>
    </section>
  );
}

function LedgerTableRow({ item }: { item: AutoDormCheckQuotaLedgerItem }) {
  const presentation = quotaLedgerPresentation(item);
  return (
    <tr>
      <td className="whitespace-nowrap font-mono text-[10.5px] text-ink-faint">
        {formatDateTime(item.occurredAt)}
      </td>
      <td>
        <LedgerUser user={item.user} />
      </td>
      <td>
        <span className={`adc-ledger-action is-${item.actionType}`}>
          {presentation.icon}
          {presentation.label}
        </span>
      </td>
      <td>
        <LedgerDelta item={item} />
      </td>
      <td>
        {item.purchase ? (
          <strong className="adc-ledger-income">
            {formatMoney(item.purchase.amountCents)}
          </strong>
        ) : (
          <span className="text-ink-faint">—</span>
        )}
      </td>
      <td>
        <p className="adc-ledger-description">{presentation.description}</p>
        <p className="adc-ledger-actor">
          {item.actionType === "purchase"
            ? "用户自助购买"
            : `操作人 ${item.actor || "管理员"}`}
          {item.reason ? ` · ${item.reason}` : ""}
        </p>
      </td>
    </tr>
  );
}

function LedgerMobileCard({ item }: { item: AutoDormCheckQuotaLedgerItem }) {
  const presentation = quotaLedgerPresentation(item);
  return (
    <article className="adc-ledger-mobile-card">
      <div className="flex items-start justify-between gap-3">
        <LedgerUser user={item.user} />
        <span className={`adc-ledger-action is-${item.actionType}`}>
          {presentation.icon}
          {presentation.label}
        </span>
      </div>
      <div className="adc-ledger-mobile-change">
        <LedgerDelta item={item} />
        {item.purchase ? (
          <strong>{formatMoney(item.purchase.amountCents)}</strong>
        ) : null}
      </div>
      <p className="adc-ledger-description">{presentation.description}</p>
      <p className="adc-ledger-actor">
        {formatDateTime(item.occurredAt)} ·{" "}
        {item.actionType === "purchase"
          ? "用户自助购买"
          : item.actor || "管理员"}
        {item.reason ? ` · ${item.reason}` : ""}
      </p>
    </article>
  );
}

function LedgerUser({ user }: { user: AutoDormCheckQuotaLedgerItem["user"] }) {
  return (
    <span className="adc-ledger-user">
      <span className="adc-avatar">{user.name.slice(0, 1)}</span>
      <span className="min-w-0">
        <strong>{user.name}</strong>
        <small>{user.studentNumber}</small>
      </span>
    </span>
  );
}

function LedgerDelta({ item }: { item: AutoDormCheckQuotaLedgerItem }) {
  const delta = item.walletDelta;
  return (
    <span
      className={`adc-ledger-delta ${delta > 0 ? "is-positive" : delta < 0 ? "is-negative" : "is-neutral"}`}
    >
      {delta === 0
        ? "余额不变"
        : `${delta > 0 ? "+" : "−"}${formatCreditAmount(
            Math.abs(delta),
            item.quotaKind,
          )}`}
    </span>
  );
}

function quotaLedgerPresentation(item: AutoDormCheckQuotaLedgerItem): {
  label: string;
  description: string;
  icon: ReactNode;
} {
  if (item.actionType === "purchase") {
    return {
      label: "用户购买",
      description: item.purchase?.planLabel || "打卡套餐",
      icon: <Banknote size={12} />,
    };
  }
  if (item.actionType === "grant") {
    const scopes: Record<string, string> = {
      all_users: "所有人",
      selected_users: "指定用户",
      paid_any: "所有付费用户",
      paid_plan: "指定付费套餐用户",
    };
    const scope = scopes[item.grant?.scope || ""] || "指定范围";
    return {
      label: "管理员发放",
      description: `${scope}${item.grant?.includeFuture ? " · 含后续符合用户" : ""}`,
      icon: <Gift size={12} />,
    };
  }
  if (item.actionType === "adjustment") {
    const operation = item.adjustment?.operation;
    const description =
      operation === "set"
        ? `设置为 ${formatCreditAmount(
            item.adjustment?.targetAmount || 0,
            item.quotaKind,
          )}`
        : operation === "deduct"
          ? "扣减可用额度"
          : "增加可用额度";
    return {
      label: "管理员修改",
      description,
      icon: <SlidersHorizontal size={12} />,
    };
  }
  return {
    label: "管理员撤回",
    description: "撤回该笔来源的未使用额度",
    icon: <RotateCcw size={12} />,
  };
}

function formatMoney(cents: number): string {
  return `¥${(Math.max(0, Number(cents) || 0) / 100).toFixed(2)}`;
}

function QuotaAccountsSection({
  query,
  onQueryChange,
  onSearch,
  result,
  loading,
  error,
  onRetry,
  onPageChange,
  onSelect,
  onChanged,
  paymentEnabled,
}: {
  query: string;
  onQueryChange: (value: string) => void;
  onSearch: () => void;
  result: Paginated<AutoDormCheckQuotaUser> | null;
  loading: boolean;
  error: Error | null;
  onRetry: () => Promise<unknown>;
  onPageChange: (page: number) => void;
  onSelect: (userId: string) => void;
  onChanged: () => Promise<unknown>;
  paymentEnabled: boolean | null;
}) {
  const freeMode = paymentEnabled === false;
  const visibleAccounts = freeMode ? [] : result?.items || [];
  const visibleTotal = freeMode ? 0 : (result?.pagination.total ?? null);
  const visibleUses = visibleAccounts.reduce(
    (total, account) => total + account.balance.remainingUses,
    0,
  );
  const visibleDays = visibleAccounts.reduce(
    (total, account) => total + account.balance.remainingDays,
    0,
  );
  const pausedAccounts = visibleAccounts.filter(
    (account) => account.balance.state === "payment_paused",
  ).length;

  return (
    <section className="adc-workspace rise-in rise-in-2">
      {!freeMode && error ? (
        <ErrorState error={error} onRetry={() => void onRetry()} />
      ) : null}
      <section className="adc-card overflow-hidden">
        <div className="adc-card-toolbar">
          <div className="adc-toolbar-title">
            <span className="adc-toolbar-icon">
              <WalletCards size={16} />
            </span>
            <div>
              <div className="flex items-baseline gap-2">
                <h2>额度账户</h2>
                <span className="adc-count">{visibleTotal ?? "—"}</span>
              </div>
            </div>
          </div>
          <div className="adc-toolbar-actions">
            {!freeMode ? (
              <AutoDormSearch
                value={query}
                onChange={onQueryChange}
                onSubmit={onSearch}
                placeholder="搜索姓名或学号…"
              />
            ) : null}
          </div>
        </div>

        {!freeMode ? (
          <div className="adc-metric-strip">
            <span>
              有额度用户 <b>{visibleTotal ?? "—"}</b>
            </span>
            <span>
              本页可用次数 <b>{visibleUses}</b>
            </span>
            <span>
              本页可用时长 <b>{visibleDays} 天</b>
            </span>
            {pausedAccounts ? (
              <span className="is-warning">
                <CircleAlert size={13} /> 暂停消耗 <b>{pausedAccounts}</b>
              </span>
            ) : null}
          </div>
        ) : null}

        {!freeMode && loading ? (
          <LoadingState rows={8} />
        ) : !freeMode && result?.items.length ? (
          <>
            <div className="adc-mobile-list md:hidden">
              {result.items.map((account) => (
                <QuotaAccountMobileCard
                  key={account.user.id}
                  account={account}
                  onSelect={() => onSelect(account.user.id)}
                  onChanged={onChanged}
                />
              ))}
            </div>
            <div className="hidden overflow-x-auto md:block">
              <table className="adc-table min-w-[900px]">
                <thead>
                  <tr>
                    <th className="w-[240px]">用户</th>
                    <th className="w-[245px]">打卡额度</th>
                    <th>账户状态</th>
                    <th>额度来源</th>
                    <th>最近活动</th>
                    <th className="w-[72px] text-right">明细</th>
                  </tr>
                </thead>
                <tbody>
                  {result.items.map((account) => (
                    <QuotaAccountRow
                      key={account.user.id}
                      account={account}
                      onSelect={() => onSelect(account.user.id)}
                      onChanged={onChanged}
                    />
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              page={result.pagination.page}
              pages={result.pagination.totalPages}
              total={result.pagination.total}
              onChange={onPageChange}
              className="adc-card-footer"
            />
          </>
        ) : (
          <EmptyState
            title={freeMode ? "当前为免费模式" : "没有额度账户"}
            description={
              freeMode
                ? "开启付费后显示持有可用额度的用户。"
                : query.trim()
                  ? "没有匹配的姓名或学号，请调整关键词。"
                  : "当前没有用户持有可用额度。"
            }
          />
        )}
      </section>
    </section>
  );
}

function QuotaAccountMobileCard({
  account,
  onSelect,
  onChanged,
}: {
  account: AutoDormCheckQuotaUser;
  onSelect: () => void;
  onChanged: () => Promise<unknown>;
}) {
  const state = quotaBalanceState(account.balance.state);
  const reservedUses = account.balance.reservedUses || 0;
  return (
    <article className="adc-mobile-card cursor-pointer" onClick={onSelect}>
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onSelect();
        }}
        className="flex w-full items-start gap-3 text-left"
      >
        <span className="adc-avatar">{account.user.name.slice(0, 1)}</span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center justify-between gap-3">
            <strong className="truncate text-[12.5px] font-medium text-ink">
              {account.user.name}
            </strong>
            <ChevronRight size={15} className="shrink-0 text-ink-faint" />
          </span>
          <span className="mt-0.5 block font-mono text-[10.5px] text-ink-faint">
            {account.user.studentNumber}
          </span>
        </span>
      </button>

      <div className="adc-mobile-quota">
        <InlineQuotaEditor
          user={account.user}
          balance={account.balance}
          onChanged={onChanged}
        />
        <div className="mt-2 flex items-center justify-between gap-3 text-[10px] text-ink-faint">
          <span>
            {state || "账户正常"}
            {reservedUses ? " · " + reservedUses + " 次执行中" : ""}
          </span>
          <span>{account.creditCount} 笔来源</span>
        </div>
      </div>

      <div className="mt-3 flex items-center justify-between gap-3 text-[10px] text-ink-faint">
        <span>最近活动</span>
        <span>{formatDateTime(account.lastActivityAt)}</span>
      </div>
    </article>
  );
}

function QuotaAccountRow({
  account,
  onSelect,
  onChanged,
}: {
  account: AutoDormCheckQuotaUser;
  onSelect: () => void;
  onChanged: () => Promise<unknown>;
}) {
  const reservedUses = account.balance.reservedUses || 0;
  const state = quotaBalanceState(account.balance.state);
  return (
    <tr className="cursor-pointer" onClick={onSelect}>
      <td>
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onSelect();
          }}
          className="flex w-full items-center gap-3 rounded-lg text-left"
        >
          <span className="adc-avatar">{account.user.name.slice(0, 1)}</span>
          <span className="min-w-0">
            <strong className="block truncate text-[12.5px] font-medium text-ink">
              {account.user.name}
            </strong>
            <small className="mt-0.5 block font-mono text-[10.5px] text-ink-faint">
              {account.user.studentNumber}
            </small>
          </span>
        </button>
      </td>
      <td>
        <InlineQuotaEditor
          user={account.user}
          balance={account.balance}
          onChanged={onChanged}
        />
      </td>
      <td>
        <span
          className={`adc-account-state ${account.balance.state === "active" ? "is-positive" : ""}`}
        >
          <i />
          {state || "账户正常"}
        </span>
        {reservedUses ? (
          <small className="mt-1 block text-[10.5px] text-clay-deep">
            {reservedUses} 次执行中
          </small>
        ) : null}
      </td>
      <td className="text-ink-soft">{account.creditCount} 笔</td>
      <td className="whitespace-nowrap font-mono text-[11px] text-ink-faint">
        {formatDateTime(account.lastActivityAt)}
      </td>
      <td className="text-right">
        <button
          type="button"
          aria-label={"查看 " + account.user.name + " 的额度明细"}
          onClick={(event) => {
            event.stopPropagation();
            onSelect();
          }}
          className="adc-row-link"
        >
          <ChevronRight size={15} />
        </button>
      </td>
    </tr>
  );
}

function InlineQuotaEditor({
  user,
  balance,
  onChanged,
}: {
  user: AutoDormCheckQuotaUser["user"];
  balance: AutoDormCheckQuotaUser["balance"];
  onChanged: () => Promise<unknown>;
}) {
  return (
    <div
      className="adc-inline-quota"
      onClick={(event) => event.stopPropagation()}
    >
      <span className="adc-quota-slot">
        <InlineQuotaValue
          user={user}
          kind="count"
          value={balance.remainingUses}
          maximum={100000}
          unit="次"
          onChanged={onChanged}
        />
      </span>
      <span className="adc-quota-divider">/</span>
      <span className="adc-quota-slot">
        <InlineQuotaValue
          user={user}
          kind="time"
          value={balance.remainingDays}
          maximum={3650}
          unit="天"
          onChanged={onChanged}
        />
      </span>
    </div>
  );
}

export function InlineQuotaValue({
  user,
  kind,
  value,
  maximum,
  unit,
  onChanged,
  onSave,
}: {
  user: AutoDormCheckQuotaUser["user"];
  kind: "time" | "count";
  value: number;
  maximum: number;
  unit: "天" | "次";
  onChanged: () => Promise<unknown>;
  onSave?: (
    target: number,
    reason: string,
    idempotencyKey: string,
  ) => Promise<unknown>;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!editing) setDraft(String(value));
  }, [editing, value]);

  useEffect(() => {
    if (!editing) return;
    inputRef.current?.focus();
    inputRef.current?.select();
  }, [editing]);

  const cancel = () => {
    if (saving) return;
    setDraft(String(value));
    setEditing(false);
  };

  const commit = async () => {
    if (!editing || saving) return;
    const target = parseDirectQuotaValue(draft, maximum);
    if (target === null) {
      toast.error(`请输入 0 到 ${maximum} 的整数`);
      cancel();
      return;
    }
    if (target === value) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      const reason = `额度账户列表直接修改：${value} ${unit} → ${target} ${unit}`;
      const key = window.crypto.randomUUID();
      if (onSave) await onSave(target, reason, key);
      else
        await api.adjustAutoDormCheckQuota(
          user.id,
          {
            quotaKind: kind,
            operation: "set",
            quotaAmount: target,
            reason,
          },
          key,
        );
      setDraft(String(target));
      setEditing(false);
      await onChanged();
      toast.success(
        `${user.name}的${unit === "次" ? "次数" : "时长"}额度已更新`,
      );
    } catch (caught) {
      setDraft(String(value));
      setEditing(false);
      toast.error(caught instanceof Error ? caught.message : "额度修改失败");
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <span className="adc-quota-editing">
        <input
          ref={inputRef}
          type="number"
          inputMode="numeric"
          min={0}
          max={maximum}
          step={1}
          value={draft}
          disabled={saving}
          aria-label={`${user.name}的剩余${unit === "次" ? "次数" : "天数"}`}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={() => void commit()}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              event.currentTarget.blur();
            }
            if (event.key === "Escape") {
              event.preventDefault();
              cancel();
            }
          }}
        />
        <span>{unit}</span>
        {saving ? <RefreshCw size={11} className="animate-spin" /> : null}
      </span>
    );
  }

  return (
    <button
      type="button"
      className={`adc-quota-value ${value === 0 ? "is-empty" : ""}`}
      title={`点击修改${unit === "次" ? "次数" : "天数"}额度`}
      onClick={() => setEditing(true)}
    >
      <strong>{value}</strong>
      <span>{unit}</span>
    </button>
  );
}

export function parseDirectQuotaValue(
  value: string,
  maximum: number,
): number | null {
  const normalized = value.trim();
  if (!/^\d+$/.test(normalized)) return null;
  const amount = Number(normalized);
  return Number.isSafeInteger(amount) && amount >= 0 && amount <= maximum
    ? amount
    : null;
}

function QuotaUserDetailDialog({
  userId,
  onClose,
  onChanged,
}: {
  userId: string | null;
  onClose: () => void;
  onChanged: () => Promise<void>;
}) {
  const [renderedUserId, setRenderedUserId] = useState<string | null>(userId);
  useEffect(() => {
    if (userId) setRenderedUserId(userId);
  }, [userId]);
  const displayedUserId = userId || renderedUserId;
  if (!displayedUserId) return null;
  return (
    <QuotaUserDetailDialogContent
      key={displayedUserId}
      open={Boolean(userId)}
      userId={displayedUserId}
      onClose={onClose}
      onChanged={onChanged}
      onAfterClose={() => {
        if (!userId) setRenderedUserId(null);
      }}
    />
  );
}

function QuotaUserDetailDialogContent({
  open,
  userId,
  onClose,
  onChanged,
  onAfterClose,
}: {
  open: boolean;
  userId: string;
  onClose: () => void;
  onChanged: () => Promise<void>;
  onAfterClose: () => void;
}) {
  const titleId = useId();
  const [creditPage, setCreditPage] = useState(1);
  const [activityPage, setActivityPage] = useState(1);
  const [revokeCredit, setRevokeCredit] =
    useState<AutoDormCheckQuotaCredit | null>(null);
  const [revokeReason, setRevokeReason] = useState("");
  const [revoking, setRevoking] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const revokeAttempt = useRef<{
    fingerprint: string;
    key: string;
  } | null>(null);
  const motionState = useDrawerMotion(open, { onAfterClose });
  const detail = useResource(
    () =>
      api.autoDormCheckQuotaUser(userId, {
        creditPage,
        creditPageSize: 6,
        activityPage,
        activityPageSize: 6,
      }),
    [userId, creditPage, activityPage],
  );

  const close = () => {
    if (!revoking) onClose();
  };

  useEffect(() => {
    if (!open) return undefined;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const body = document.body;
    const previousOverflow = body.style.overflow;
    const previousPaddingRight = body.style.paddingRight;
    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;
    if (scrollbarWidth > 0) {
      const currentPadding = Number.parseFloat(
        window.getComputedStyle(body).paddingRight,
      );
      body.style.paddingRight = `${(Number.isFinite(currentPadding) ? currentPadding : 0) + scrollbarWidth}px`;
    }
    body.style.overflow = "hidden";
    const focusFrame = window.requestAnimationFrame(() => {
      closeButtonRef.current?.focus({ preventScroll: true });
    });

    return () => {
      window.cancelAnimationFrame(focusFrame);
      body.style.overflow = previousOverflow;
      body.style.paddingRight = previousPaddingRight;
      if (previouslyFocused?.isConnected) {
        previouslyFocused.focus({ preventScroll: true });
      }
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        if (revokeCredit) cancelRevoke();
        else close();
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = Array.from(
        panelRef.current?.querySelectorAll<HTMLElement>(
          'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ) || [],
      ).filter((element) => element.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  });

  const cancelRevoke = () => {
    if (revoking) return;
    setRevokeCredit(null);
    setRevokeReason("");
    revokeAttempt.current = null;
  };

  const revoke = async () => {
    const reason = revokeReason.trim();
    if (!revokeCredit || !reason || revoking) return;
    const fingerprint = `${revokeCredit.id}:${reason}`;
    const attempt =
      revokeAttempt.current?.fingerprint === fingerprint
        ? revokeAttempt.current
        : { fingerprint, key: window.crypto.randomUUID() };
    revokeAttempt.current = attempt;
    setRevoking(true);
    try {
      await api.revokeAutoDormCheckCredit(
        userId,
        revokeCredit.id,
        reason,
        attempt.key,
      );
      toast.success("已撤销这笔额度的剩余部分");
      setRevokeCredit(null);
      setRevokeReason("");
      revokeAttempt.current = null;
      await Promise.all([detail.refresh(), onChanged()]);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "额度撤销失败");
    } finally {
      setRevoking(false);
    }
  };

  return createPortal(
    <div
      data-state={motionState}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      className="quota-detail-drawer fixed inset-0 z-[80] h-dvh w-screen overflow-hidden text-ink"
      onClick={(event) => {
        if (event.target !== event.currentTarget) return;
        if (revokeCredit) cancelRevoke();
        else close();
      }}
    >
      <div
        ref={panelRef}
        className="quota-detail-drawer__panel absolute inset-y-0 right-0 flex h-dvh w-[min(760px,100vw)] flex-col overflow-hidden border-l border-sand-light bg-paper shadow-2xl"
      >
        <header className="flex flex-none items-start gap-4 border-b border-sand-light/70 px-5 py-5 sm:px-7">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-clay-soft text-clay-deep">
            <ReceiptText size={18} />
          </span>
          <div className="min-w-0 flex-1">
            <h2 id={titleId} className="font-display text-lg font-medium">
              {detail.data?.user.name || "额度账户明细"}
            </h2>
            <p className="mt-1 font-mono text-[11px] text-ink-faint">
              {detail.data?.user.studentNumber || "—"}
            </p>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            aria-label="关闭详情"
            disabled={revoking}
            onClick={close}
            className="rounded-lg p-2 text-ink-faint transition hover:bg-peach hover:text-ink disabled:opacity-40"
          >
            <X size={18} />
          </button>
        </header>

        <div className="quota-detail-drawer__body scrollbar-thin min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-7 sm:py-6">
          {detail.error ? (
            <ErrorState
              error={detail.error}
              onRetry={() => void detail.refresh()}
            />
          ) : detail.loading && !detail.data ? (
            <LoadingState rows={10} />
          ) : detail.data ? (
            <QuotaUserDetail detail={detail.data} onRevoke={setRevokeCredit} />
          ) : null}
        </div>

        {detail.data ? (
          <div className="flex flex-none flex-col gap-2 border-t border-sand-light/70 bg-cream/45 px-5 pb-[calc(12px+env(safe-area-inset-bottom))] pt-3 sm:flex-row sm:items-center sm:justify-between sm:px-7">
            <CompactPager
              label="额度来源"
              page={detail.data.credits.pagination.page}
              pages={detail.data.credits.pagination.totalPages}
              onChange={setCreditPage}
            />
            <CompactPager
              label="活动流水"
              page={detail.data.activities.pagination.page}
              pages={detail.data.activities.pagination.totalPages}
              onChange={setActivityPage}
            />
          </div>
        ) : null}

        {revokeCredit ? (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-ink/35 p-4 backdrop-blur-[2px]">
            <div
              role="dialog"
              aria-modal="true"
              aria-label="撤销剩余额度"
              className="w-full max-w-md rounded-2xl border border-sand-light bg-paper p-5 shadow-2xl sm:p-6"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#F3DDD3] text-destructive">
                <RotateCcw size={18} />
              </span>
              <h3 className="mt-4 font-display text-lg font-medium text-ink">
                撤销剩余额度？
              </h3>
              <p className="mt-2 text-[12px] leading-relaxed text-ink-soft">
                {revokeCredit.remainingAmount > 0 ? (
                  <>
                    将立即撤销此来源可用的
                    <strong className="mx-1 text-ink">
                      {formatCreditAmount(
                        revokeCredit.remainingAmount,
                        revokeCredit.quotaKind,
                      )}
                    </strong>
                  </>
                ) : (
                  "此来源当前没有可用余额，将标记为已撤销"
                )}
                {revokeCredit.reservedAmount > 0
                  ? `，另有 ${formatCreditAmount(revokeCredit.reservedAmount, revokeCredit.quotaKind)} 正在执行，任务结束后不会返还`
                  : ""}
                。已使用部分不会回退，购买款项不会退款，历史记录也不会删除。
              </p>
              <label className="mt-4 block text-[11.5px] font-medium text-ink">
                撤销原因
                <textarea
                  autoFocus
                  rows={3}
                  maxLength={500}
                  value={revokeReason}
                  onChange={(event) => {
                    setRevokeReason(event.target.value);
                    revokeAttempt.current = null;
                  }}
                  placeholder="必填，将保留在追溯记录中"
                  className="mt-2 w-full resize-none rounded-xl border border-sand bg-cream/35 px-3 py-2.5 text-[12.5px] font-normal text-ink outline-none transition placeholder:text-ink-faint/65 focus:border-clay"
                />
              </label>
              <div className="mt-5 flex justify-end gap-2">
                <ActionButton
                  tone="ghost"
                  disabled={revoking}
                  onClick={cancelRevoke}
                >
                  取消
                </ActionButton>
                <ActionButton
                  tone="danger"
                  busy={revoking}
                  disabled={!revokeReason.trim()}
                  onClick={() => void revoke()}
                >
                  确认撤销
                </ActionButton>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}

function QuotaUserDetail({
  detail,
  onRevoke,
}: {
  detail: AutoDormCheckQuotaUserDetail;
  onRevoke: (credit: AutoDormCheckQuotaCredit) => void;
}) {
  const reservedUses = detail.balance.reservedUses || 0;
  return (
    <div className="space-y-7">
      <section>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h3 className="text-[13px] font-medium text-ink">余额摘要</h3>
            <p className="mt-0.5 text-[11px] text-ink-faint">
              时长额度连续计时，不因个人关闭自动打卡暂停；次数额度仅成功后扣减
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {quotaBalanceState(detail.balance.state) ? (
              <Tag tone={detail.balance.state === "active" ? "sage" : "clay"}>
                {quotaBalanceState(detail.balance.state)}
              </Tag>
            ) : null}
          </div>
        </div>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <BalanceCard
            icon={<CalendarClock size={17} />}
            label="剩余有效期"
            value={formatQuotaDuration(detail.balance.remainingSeconds)}
            detail={`连续计时 ${formatQuotaDuration(detail.totals.consumedSeconds)}${detail.totals.adjustedSeconds ? ` · 管理员扣减 ${formatQuotaDuration(detail.totals.adjustedSeconds)}` : ""}`}
          />
          <BalanceCard
            icon={<Activity size={17} />}
            label="剩余次数"
            value={`${detail.balance.remainingUses} 次`}
            detail={`累计使用 ${detail.totals.consumedUses} 次${detail.totals.adjustedUses ? ` · 管理员扣减 ${detail.totals.adjustedUses} 次` : ""}${reservedUses ? ` · 执行中 ${reservedUses} 次` : ""}`}
          />
        </div>
      </section>

      <section>
        <div className="flex items-end justify-between gap-3">
          <div>
            <h3 className="text-[13px] font-medium text-ink">充值与发放</h3>
            <p className="mt-0.5 text-[11px] text-ink-faint">
              仅展示当前仍可使用的额度，可撤销项只影响尚未使用的余额
            </p>
          </div>
          <span className="text-[10.5px] text-ink-faint">
            共 {detail.credits.pagination.total} 笔
          </span>
        </div>
        <div className="mt-3 space-y-2.5">
          {detail.credits.items.length ? (
            detail.credits.items.map((credit) => (
              <QuotaCreditCard
                key={credit.id}
                credit={credit}
                onRevoke={() => onRevoke(credit)}
              />
            ))
          ) : (
            <div className="rounded-xl border border-dashed border-sand px-4 py-8 text-center text-[12px] text-ink-faint">
              暂无可用额度
            </div>
          )}
        </div>
      </section>

      <section>
        <div className="flex items-end justify-between gap-3">
          <div>
            <h3 className="text-[13px] font-medium text-ink">使用与变更流水</h3>
            <p className="mt-0.5 text-[11px] text-ink-faint">
              购买、发放、成功扣次、补偿、调整与撤销
            </p>
          </div>
          <span className="text-[10.5px] text-ink-faint">
            共 {detail.activities.pagination.total} 条
          </span>
        </div>
        <div className="scrollbar-thin relative mt-3 max-h-[336px] space-y-0 overflow-y-auto pr-2 before:absolute before:bottom-4 before:left-[13px] before:top-4 before:w-px before:bg-sand-light">
          {detail.activities.items.length ? (
            detail.activities.items.map((activity) => (
              <QuotaActivityRow key={activity.id} activity={activity} />
            ))
          ) : (
            <div className="rounded-xl border border-dashed border-sand px-4 py-8 text-center text-[12px] text-ink-faint">
              暂无活动流水
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function BalanceCard({
  icon,
  label,
  value,
  detail,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  detail: string;
}) {
  return (
    <div className="rounded-xl border border-sand-light bg-cream/45 p-4">
      <div className="flex items-center gap-2 text-[11px] text-ink-faint">
        <span className="text-clay">{icon}</span>
        {label}
      </div>
      <strong className="mt-3 block font-display text-xl font-medium text-ink">
        {value}
      </strong>
      <p className="mt-1 text-[10.5px] text-ink-faint">{detail}</p>
    </div>
  );
}

function QuotaCreditCard({
  credit,
  onRevoke,
}: {
  credit: AutoDormCheckQuotaCredit;
  onRevoke: () => void;
}) {
  const source = quotaCreditSource(credit);
  const status = quotaCreditStatus(credit.status);
  const purchaseDescription = credit.purchase
    ? `${credit.purchase.planLabel} · ¥${(credit.purchase.amountCents / 100).toFixed(2)} · 支付于 ${formatDateTime(credit.purchase.paidAt)}`
    : null;
  const adminGrantDescription = credit.adminGrant
    ? `${credit.adminGrant.quotaAmount ? `自定义 ${credit.adminGrant.quotaAmount} ${credit.adminGrant.quotaKind === "time" ? "天" : "次"} · ` : credit.adminGrant.planLabel ? `${credit.adminGrant.planLabel} · ` : ""}由 ${credit.adminGrant.createdBy || credit.createdBy || "管理员"} 发放`
    : null;
  const adjustmentDescription =
    credit.sourceType === "admin_adjustment"
      ? `管理员增加 ${credit.sourceDetail?.quotaAmount || "—"} ${credit.quotaKind === "time" ? "天" : "次"} · ${credit.sourceDetail?.createdBy || credit.createdBy || "管理员"}${credit.sourceDetail?.reason ? ` · ${credit.sourceDetail.reason}` : ""}`
      : null;
  const compensationDescription =
    credit.sourceType === "task_failure_compensation"
      ? `任务 #${(credit.sourceTaskId || credit.sourceDetail?.taskId || "").slice(-8) || "—"} · 时间额度顺延 ${formatCreditAmount(credit.originalAmount, credit.quotaKind)}`
      : null;
  return (
    <article className="rounded-xl border border-sand-light bg-paper p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <strong className="text-[12.5px] font-medium text-ink">
              {source.label}
            </strong>
            <Tag tone={source.tone}>
              {credit.quotaKind === "time" ? "时长" : "次数"}
            </Tag>
            <Tag tone={status.tone}>{status.label}</Tag>
          </div>
          <p className="mt-1.5 text-[11px] text-ink-soft">
            {purchaseDescription ||
              adminGrantDescription ||
              adjustmentDescription ||
              compensationDescription ||
              (credit.sourceType === "admin_grant"
                ? `由 ${credit.createdBy || "管理员"} 发放`
                : "历史额度迁移记录")}
          </p>
          <p className="mt-1 text-[10.5px] text-ink-faint">
            入账于 {formatDateTime(credit.grantedAt)}
            {credit.revokeReason ? ` · 撤销原因：${credit.revokeReason}` : ""}
          </p>
        </div>
        {credit.revocable ? (
          <ActionButton tone="danger" className="shrink-0" onClick={onRevoke}>
            <RotateCcw size={12} />
            撤销剩余
          </ActionButton>
        ) : null}
      </div>
      <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 border-t border-sand-light/65 pt-3 sm:grid-cols-6">
        <CreditMetric
          label="原始"
          value={formatCreditAmount(credit.originalAmount, credit.quotaKind)}
        />
        <CreditMetric
          label={credit.quotaKind === "time" ? "已计时" : "已用"}
          value={formatCreditAmount(credit.consumedAmount, credit.quotaKind)}
        />
        <CreditMetric
          label="已撤销"
          value={formatCreditAmount(credit.revokedAmount, credit.quotaKind)}
        />
        <CreditMetric
          label="管理员扣减"
          value={formatCreditAmount(
            credit.adjustedAmount || 0,
            credit.quotaKind,
          )}
        />
        <CreditMetric
          label="可用"
          value={formatCreditAmount(credit.remainingAmount, credit.quotaKind)}
          emphasized
        />
        <CreditMetric
          label="执行中预留"
          value={formatCreditAmount(credit.reservedAmount, credit.quotaKind)}
        />
      </div>
    </article>
  );
}

function CreditMetric({
  label,
  value,
  emphasized = false,
}: {
  label: string;
  value: string;
  emphasized?: boolean;
}) {
  return (
    <div>
      <p className="text-[10px] text-ink-faint">{label}</p>
      <p
        className={`mt-0.5 font-mono text-[11.5px] ${emphasized ? "font-medium text-clay-deep" : "text-ink-soft"}`}
      >
        {value}
      </p>
    </div>
  );
}

function QuotaActivityRow({
  activity,
}: {
  activity: AutoDormCheckQuotaActivity;
}) {
  const presentation = quotaActivityPresentation(activity);
  const walletDelta = quotaActivityWalletDelta(activity);
  return (
    <article className="relative flex gap-3 py-3">
      <span
        className={`relative z-10 mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${presentation.className}`}
      >
        {presentation.icon}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <strong className="text-[12px] font-medium text-ink">
            {presentation.label}
          </strong>
          <span
            className={`font-mono text-[11.5px] font-medium ${walletDelta > 0 ? "text-sage" : walletDelta < 0 ? "text-destructive" : "text-ink-faint"}`}
          >
            {walletDelta === 0
              ? `余额不变 · ${formatCreditAmount(activity.amount, activity.quotaKind)}`
              : `${walletDelta > 0 ? "+" : "−"}${formatCreditAmount(Math.abs(walletDelta), activity.quotaKind)}`}
          </span>
        </div>
        <p className="mt-0.5 text-[10.5px] leading-relaxed text-ink-faint">
          {activity.reason || presentation.description}
          {activity.actor && activity.actor !== "system"
            ? ` · 操作人 ${activity.actor}`
            : ""}
          {activity.taskId ? ` · 任务 #${activity.taskId.slice(-8)}` : ""}
          {activity.adjustmentId
            ? ` · 调整 #${activity.adjustmentId.slice(-8)}`
            : ""}
          {` · ${formatDateTime(activity.createdAt)}`}
        </p>
      </div>
    </article>
  );
}

function CompactPager({
  label,
  page,
  pages,
  onChange,
}: {
  label: string;
  page: number;
  pages: number;
  onChange: (page: number) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3 text-[10.5px] text-ink-faint sm:justify-start">
      <span>{label}</span>
      <div className="flex items-center gap-1">
        <button
          type="button"
          disabled={page <= 1}
          onClick={() => onChange(page - 1)}
          className="rounded-full px-2 py-1 transition hover:bg-paper disabled:opacity-35"
        >
          上一页
        </button>
        <span className="min-w-10 text-center font-mono">
          {page}/{Math.max(1, pages)}
        </span>
        <button
          type="button"
          disabled={page >= pages || pages === 0}
          onClick={() => onChange(page + 1)}
          className="rounded-full px-2 py-1 transition hover:bg-paper disabled:opacity-35"
        >
          下一页
        </button>
      </div>
    </div>
  );
}

function quotaBalanceState(state: AutoDormCheckQuotaUser["balance"]["state"]) {
  const labels = {
    active: "正常消耗",
    payment_paused: "额度已冻结，暂停消耗",
    academic_break: "校历暂停，时长顺延",
    empty: "暂无可用额度",
  };
  return state ? labels[state] : null;
}

function quotaCreditSource(credit: AutoDormCheckQuotaCredit): {
  label: string;
  tone: "neutral" | "clay" | "sage";
} {
  if (credit.sourceType === "purchase")
    return { label: "用户购买", tone: "clay" };
  if (credit.sourceType === "admin_grant")
    return { label: "管理员发放", tone: "sage" };
  if (credit.sourceType === "admin_adjustment")
    return { label: "管理员调整", tone: "sage" };
  if (credit.sourceType === "task_failure_compensation")
    return { label: "任务补偿", tone: "sage" };
  return { label: "历史额度", tone: "neutral" };
}

function quotaCreditStatus(status: AutoDormCheckQuotaCredit["status"]): {
  label: string;
  tone: "neutral" | "sage" | "danger";
} {
  if (status === "active") return { label: "可用", tone: "sage" };
  if (status === "revoked") return { label: "已撤销", tone: "danger" };
  return { label: "已用完", tone: "neutral" };
}

export function quotaActivityPresentation(
  activity: AutoDormCheckQuotaActivity,
) {
  const normalized = activity.type.toLowerCase();
  if (normalized === "identity_merge_adjustment") {
    return {
      label: "重复额度调整",
      description: "重复赠送已去重",
      className: "bg-clay-soft text-clay-deep",
      icon: <Minus size={12} />,
    };
  }
  if (normalized === "refund_reserved") {
    return {
      label: "退款冻结",
      description: "已申请退还未使用额度",
      className: "bg-clay-soft text-clay-deep",
      icon: <Activity size={12} />,
    };
  }
  if (normalized === "refund_released") {
    return {
      label: "退款关闭",
      description: "未完成退款，额度已恢复",
      className: "bg-[#E7EBE0] text-sage",
      icon: <RefreshCw size={12} />,
    };
  }
  if (normalized.includes("admin_adjustment_added")) {
    return {
      label: "管理员增加额度",
      description: "管理员直接调整可用余额",
      className: "bg-[#E7EBE0] text-sage",
      icon: <Plus size={12} />,
    };
  }
  if (normalized.includes("admin_adjustment_deducted")) {
    return {
      label: "管理员扣减额度",
      description: "管理员直接调整可用余额",
      className: "bg-clay-soft text-clay-deep",
      icon: <Minus size={12} />,
    };
  }
  if (normalized.includes("failure_compens")) {
    const noTask = activity.metadata?.compensationKind === "no_task";
    return {
      label: noTask ? "无任务补偿" : "失败补偿",
      description: noTask
        ? "今日无查寝任务，时间额度顺延 1 天"
        : "自动打卡失败，时间额度顺延 1 天",
      className: "bg-[#E7EBE0] text-sage",
      icon: <RefreshCw size={12} />,
    };
  }
  if (normalized.includes("legacy_consumption_imported")) {
    return {
      label: "历史用量折算",
      description: "升级时按既有余额折算，未再次扣减",
      className: "bg-cream-deep text-ink-soft",
      icon: <History size={12} />,
    };
  }
  if (normalized.includes("legacy_credit_imported")) {
    return {
      label: "历史余额导入",
      description: "升级时迁移既有额度余额",
      className: "bg-[#E7EBE0] text-sage",
      icon: <ReceiptText size={12} />,
    };
  }
  if (
    normalized.includes("record_imported") ||
    normalized.includes("purchase_imported") ||
    normalized.includes("grant_imported")
  ) {
    return {
      label: "历史凭证导入",
      description: "仅补充来源记录，未重复增加余额",
      className: "bg-cream-deep text-ink-soft",
      icon: <ReceiptText size={12} />,
    };
  }
  if (normalized.includes("revoke")) {
    return {
      label: "撤销剩余额度",
      description: "管理员撤销",
      className: "bg-[#F3DDD3] text-destructive",
      icon: <RotateCcw size={12} />,
    };
  }
  if (normalized.includes("release")) {
    if (activity.metadata?.restoredToBalance === false) {
      return {
        label: "预留结束，未返还",
        description: "对应额度已撤销，本次预留不再回到账户",
        className: "bg-cream-deep text-ink-soft",
        icon: <History size={12} />,
      };
    }
    return {
      label: "返还预留次数",
      description: "任务未消耗，额度已返还",
      className: "bg-[#E7EBE0] text-sage",
      icon: <RefreshCw size={12} />,
    };
  }
  if (normalized.includes("count_consumed")) {
    return {
      label: "自动打卡扣次",
      description: "自动打卡成功，扣减次数额度",
      className: "bg-clay-soft text-clay-deep",
      icon: <Activity size={12} />,
    };
  }
  if (normalized.includes("consume")) {
    return {
      label: "打卡扣减",
      description: "自动查寝任务使用额度",
      className: "bg-clay-soft text-clay-deep",
      icon: <Activity size={12} />,
    };
  }
  if (normalized.includes("reserve")) {
    return {
      label: "任务预留",
      description: "执行中的任务暂时占用次数",
      className: "bg-cream-deep text-ink-soft",
      icon: <History size={12} />,
    };
  }
  return {
    label: "额度入账",
    description: "购买或管理员发放",
    className: "bg-[#E7EBE0] text-sage",
    icon: <Gift size={12} />,
  };
}

export function quotaActivityWalletDelta(activity: AutoDormCheckQuotaActivity) {
  const type = activity.type.toLowerCase();
  if (type.includes("count_consumed")) return -Math.abs(activity.amount);
  if (typeof activity.walletDelta === "number") return activity.walletDelta;
  if (type.includes("record_imported")) return 0;
  if (type.includes("release")) {
    return activity.metadata?.restoredToBalance === true
      ? Math.abs(activity.amount)
      : 0;
  }
  if (type.includes("reserve") || type.includes("revoke")) {
    return -Math.abs(activity.amount);
  }
  if (type.includes("consume")) {
    return activity.quotaKind === "time" ? -Math.abs(activity.amount) : 0;
  }
  return Math.abs(activity.amount);
}

export function formatQuotaDuration(seconds: number): string {
  const safeSeconds = Math.max(0, Math.floor(Number(seconds) || 0));
  if (safeSeconds === 0) return "0 秒";
  const days = Math.floor(safeSeconds / 86400);
  const hours = Math.floor((safeSeconds % 86400) / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const remainderSeconds = safeSeconds % 60;
  if (days > 0) {
    const remainder = safeSeconds % 86400;
    return remainder
      ? `${days} 天 ${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(remainderSeconds).padStart(2, "0")}`
      : `${days} 天`;
  }
  if (hours > 0) {
    return `${hours} 小时 ${minutes} 分 ${remainderSeconds} 秒`;
  }
  if (minutes > 0) return `${minutes} 分 ${remainderSeconds} 秒`;
  return `${remainderSeconds} 秒`;
}

function formatCreditAmount(amount: number, kind: "time" | "count") {
  return kind === "time"
    ? formatQuotaDuration(amount)
    : `${Math.max(0, amount)} 次`;
}

export function GlobalSwitchRow({
  title,
  description,
  enabled,
  disabled,
  onChange,
  icon,
}: {
  title: string;
  description: string;
  enabled: boolean;
  disabled: boolean;
  onChange: (enabled: boolean) => void;
  icon?: ReactNode;
}) {
  return (
    <article className="adc-control-item">
      <span
        className={`adc-control-icon ${enabled ? "is-positive" : "is-neutral"}`}
      >
        {icon || <ShieldCheck size={16} />}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <h3>{title}</h3>
          <span className={`adc-mini-state ${enabled ? "is-on" : ""}`}>
            {enabled ? "开启" : "关闭"}
          </span>
        </div>
        <p>{description}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        aria-label={title}
        disabled={disabled}
        onClick={() => onChange(!enabled)}
        className={`adc-switch ${enabled ? "is-on" : ""}`}
      >
        <span />
      </button>
    </article>
  );
}

function CheckInWindowsEditor({
  settings,
  onSaved,
}: {
  settings: AutoDormCheckSettings | null;
  onSaved: (settings: AutoDormCheckSettings) => void;
}) {
  return (
    <article className="adc-control-item adc-time-control">
      <span className="adc-control-icon is-clay">
        <Clock3 size={16} />
      </span>
      <div className="adc-time-window-stack">
        <CheckInWindowEditor
          kind="preferred"
          settings={settings}
          onSaved={onSaved}
        />
        <CheckInWindowEditor
          kind="school"
          settings={settings}
          onSaved={onSaved}
        />
      </div>
    </article>
  );
}

function CheckInWindowEditor({
  kind,
  settings,
  onSaved,
}: {
  kind: "preferred" | "school";
  settings: AutoDormCheckSettings | null;
  onSaved: (settings: AutoDormCheckSettings) => void;
}) {
  const school = kind === "school";
  const startTime = school
    ? (settings?.schoolCheckInStartTime ?? "21:00")
    : (settings?.checkInStartTime ?? "21:10");
  const endTime = school
    ? (settings?.schoolCheckInEndTime ?? "23:30")
    : (settings?.checkInEndTime ?? "22:30");
  const [startDraft, setStartDraft] = useState(startTime);
  const [endDraft, setEndDraft] = useState(endTime);
  const [savedWindow, setSavedWindow] = useState({ startTime, endTime });
  const [saveState, setSaveState] = useState<
    "idle" | "pending" | "saving" | "saved" | "error"
  >("idle");
  const mounted = useRef(true);
  const saving = useRef(false);
  const savedWindowRef = useRef(savedWindow);
  const draftWindowRef = useRef({ startTime, endTime });
  const pendingWindowRef = useRef<{
    startTime: string;
    endTime: string;
  } | null>(null);
  const onSavedRef = useRef(onSaved);

  const sameWindow = (
    first: { startTime: string; endTime: string },
    second: { startTime: string; endTime: string },
  ) => first.startTime === second.startTime && first.endTime === second.endTime;

  draftWindowRef.current = {
    startTime: startDraft,
    endTime: endDraft,
  };
  onSavedRef.current = onSaved;

  useEffect(() => {
    const incomingWindow = { startTime, endTime };
    const incomingAlreadySaved = sameWindow(
      incomingWindow,
      savedWindowRef.current,
    );
    const hadUnsavedChanges = !sameWindow(
      draftWindowRef.current,
      savedWindowRef.current,
    );
    savedWindowRef.current = incomingWindow;
    setSavedWindow(incomingWindow);
    if (!hadUnsavedChanges && !saving.current && !pendingWindowRef.current) {
      draftWindowRef.current = incomingWindow;
      setStartDraft(startTime);
      setEndDraft(endTime);
      if (!incomingAlreadySaved) setSaveState("idle");
    }
  }, [startTime, endTime]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const changed = !sameWindow(
    { startTime: startDraft, endTime: endDraft },
    savedWindow,
  );
  const valid =
    startDraft < endDraft &&
    (school
      ? startDraft <= (settings?.checkInStartTime ?? "21:10") &&
        endDraft >= (settings?.checkInEndTime ?? "22:30")
      : startDraft >= (settings?.schoolCheckInStartTime ?? "21:00") &&
        endDraft <= (settings?.schoolCheckInEndTime ?? "23:30"));

  const drainPendingWindows = async () => {
    if (saving.current) return;
    saving.current = true;
    try {
      while (pendingWindowRef.current) {
        const nextWindow = pendingWindowRef.current;
        pendingWindowRef.current = null;
        if (
          nextWindow.startTime >= nextWindow.endTime ||
          sameWindow(nextWindow, savedWindowRef.current)
        ) {
          continue;
        }
        if (mounted.current) setSaveState("saving");
        try {
          const next = await api.updateAutoDormCheckSettings(
            school
              ? {
                  schoolCheckInStartTime: nextWindow.startTime,
                  schoolCheckInEndTime: nextWindow.endTime,
                }
              : {
                  checkInStartTime: nextWindow.startTime,
                  checkInEndTime: nextWindow.endTime,
                },
          );
          savedWindowRef.current = nextWindow;
          if (mounted.current) {
            setSavedWindow(nextWindow);
            onSavedRef.current(next);
            if (
              sameWindow(draftWindowRef.current, nextWindow) &&
              !pendingWindowRef.current
            ) {
              setSaveState("saved");
            }
          }
        } catch (error) {
          if (
            mounted.current &&
            sameWindow(draftWindowRef.current, nextWindow)
          ) {
            setSaveState("error");
          }
          if (mounted.current) {
            toast.error(
              error instanceof Error ? error.message : "时段保存失败",
            );
          }
        }
      }
    } finally {
      saving.current = false;
    }
  };

  useEffect(() => {
    if (!settings || !changed || !valid) {
      pendingWindowRef.current = null;
      return undefined;
    }
    const nextWindow = { startTime: startDraft, endTime: endDraft };
    pendingWindowRef.current = null;
    setSaveState("pending");
    const timer = window.setTimeout(() => {
      pendingWindowRef.current = nextWindow;
      void drainPendingWindows();
    }, 500);
    return () => window.clearTimeout(timer);
  }, [changed, endDraft, school, settings, startDraft, valid]);

  return (
    <div className="adc-time-window-row">
      <h3>{school ? "学校" : "最佳"}</h3>
      <div className="adc-time-editor">
        <TimeField
          label={`${school ? "学校" : "最佳"}开始`}
          value={startDraft}
          onChange={setStartDraft}
        />
        <span>至</span>
        <TimeField
          label={`${school ? "学校" : "最佳"}结束`}
          value={endDraft}
          onChange={setEndDraft}
        />
      </div>
      <div className="adc-time-window-feedback">
        {!valid ? (
          <span className="adc-time-error">
            {school ? "需包含最佳时段" : "需位于学校时段内"}
          </span>
        ) : null}
        {saveState === "pending" ||
        saveState === "saving" ||
        saveState === "error" ? (
          <span
            className={`adc-auto-save-state ${saveState === "error" ? "is-error" : ""}`}
            aria-live="polite"
          >
            {saveState === "pending" || saveState === "saving" ? (
              <RefreshCw size={11} className="animate-spin" />
            ) : null}
            {saveState === "error" ? "保存失败" : "保存中"}
          </span>
        ) : null}
      </div>
    </div>
  );
}

function TimeField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="adc-time-field">
      <span className="sr-only">{label}</span>
      <input
        type="time"
        step={60}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        aria-label={`${label}时间`}
      />
    </label>
  );
}

function StudentRow({
  user,
  schoolWindow,
  schoolWindowOpen,
  paymentEnabled,
  planLabel,
  onChanged,
}: {
  user: AutoDormCheckUser;
  schoolWindow: SchoolCheckInWindow | null;
  schoolWindowOpen: boolean;
  paymentEnabled: boolean | null;
  planLabel: string | null;
  onChanged: () => Promise<unknown>;
}) {
  const [expanded, setExpanded] = useState(false);
  const [checkingIn, setCheckingIn] = useState(false);
  const lastCheckIn = user.lastCheckIn;
  const credentialInvalid = user.credentialStatus === "invalid";
  const checkedInToday =
    user.checkedInToday || user.todayTaskStatus === "succeeded";
  const checkInDisabled = isImmediateCheckInDisabled(user, schoolWindowOpen);

  const checkInNow = async () => {
    if (
      checkingIn ||
      isImmediateCheckInDisabled(
        user,
        isWithinSchoolCheckInWindow(schoolWindow),
      )
    )
      return;
    setCheckingIn(true);
    try {
      const result = await api.autoDormCheckNow(user.userId);
      if (result.status === "succeeded") toast.success("打卡成功");
      else if (result.status === "retrying") toast.info("已排队");
      else if (result.status === "skipped") {
        toast.warning(result.message || "今日无需打卡");
      } else {
        toast.error(result.message || "打卡失败");
      }
      await onChanged();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "立即打卡失败");
    } finally {
      setCheckingIn(false);
    }
  };
  return (
    <article className={`adc-user-row-shell ${expanded ? "is-expanded" : ""}`}>
      <div className="adc-user-grid adc-user-row">
        <div className="adc-user-identity" data-label="用户">
          <span
            className={`adc-avatar ${user.todayTaskStatus === "failed" ? "is-danger" : ""}`}
          >
            {user.displayName.slice(0, 1)}
          </span>
          <div className="min-w-0">
            <div className="flex min-w-0 items-center gap-2">
              <strong className="truncate">{user.displayName}</strong>
              {credentialInvalid ? (
                <span
                  className="adc-credential-warning"
                  title="凭据失效"
                  role="img"
                  aria-label="凭据失效"
                >
                  <CircleAlert size={14} aria-hidden="true" />
                </span>
              ) : null}
              {!user.accountActive ? (
                <span className="adc-plan-tag is-danger">账号停用</span>
              ) : user.isPaidUser ? (
                <span className="adc-plan-tag">{planLabel || "付费用户"}</span>
              ) : null}
            </div>
            <small className="adc-student-number">
              {user.studentNumber || "—"}
            </small>
            <small className="adc-grade-class">
              {[user.grade, user.className].filter(Boolean).join(" · ") || "—"}
            </small>
          </div>
        </div>

        <div className="adc-user-meta" data-label="开启时间">
          <strong className="font-mono">
            {formatDateTime(user.enabledAt)}
          </strong>
        </div>

        <div className="adc-quota-cell" data-label="打卡额度">
          <InlineQuotaEditor
            user={{
              id: user.userId,
              studentNumber: user.studentNumber ?? null,
              account: user.account,
              name: user.displayName,
            }}
            balance={user.quotaBalance}
            onChanged={onChanged}
          />
          {user.quotaBalance.reservedUses ? (
            <small>{user.quotaBalance.reservedUses} 次执行中</small>
          ) : null}
        </div>

        <div className="adc-location-cell" data-label="上次打卡位置">
          {lastCheckIn ? (
            <>
              <div className="adc-location-title">
                <MapPin size={13} />
                <strong title={lastCheckIn.locationName}>
                  {compactLocationName(lastCheckIn.locationName)}
                </strong>
              </div>
              <small>{formatDateTime(lastCheckIn.checkedAt)}</small>
              <small className="font-mono">{locationDetail(lastCheckIn)}</small>
            </>
          ) : (
            <span className="adc-empty-location">暂无打卡记录</span>
          )}
        </div>

        <div className="adc-recent-tasks" data-label="最近任务">
          {user.recentTasks.length ? (
            user.recentTasks.slice(0, 3).map((task) => {
              const presentation = autoDormCheckTaskPresentation(
                task,
                paymentEnabled,
                user.quotaBalance,
              );
              const statusClass =
                presentation.label === "将跳过" ? "skipped" : task.status;
              return (
                <div key={task.id}>
                  <time>{formatTaskTime(task)}</time>
                  <span className={`adc-task-state is-${statusClass}`}>
                    <i />
                    {presentation.label}
                  </span>
                </div>
              );
            })
          ) : (
            <span className="adc-empty-location">暂无任务</span>
          )}
        </div>

        <div className="adc-user-actions" data-label="操作">
          <button
            type="button"
            className="adc-button adc-button-dark"
            disabled={checkInDisabled || checkingIn}
            title={
              checkedInToday
                ? "该用户今天已打卡"
                : !user.accountActive
                  ? "该账号已停用"
                  : user.todayTaskStatus === "running"
                    ? "该用户正在打卡"
                    : !schoolWindowOpen
                      ? schoolWindow
                        ? "当前不在学校查寝时段"
                        : "正在读取查寝时段"
                      : undefined
            }
            onClick={() => void checkInNow()}
          >
            {checkingIn ? (
              <RefreshCw size={13} className="animate-spin" />
            ) : (
              <Zap size={13} />
            )}
            {checkingIn ? "定位中" : "立即打卡"}
          </button>
          <button
            type="button"
            className="adc-expand-button"
            aria-label={expanded ? "收起历史" : "查看任务与打卡历史"}
            aria-expanded={expanded}
            onClick={() => setExpanded((current) => !current)}
          >
            {expanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
          </button>
        </div>
      </div>

      {expanded ? (
        <div className="adc-user-history">
          <StudentHistory
            userId={user.userId}
            paymentEnabled={paymentEnabled}
            quotaBalance={user.quotaBalance}
          />
        </div>
      ) : null}
    </article>
  );
}

function StudentHistory({
  userId,
  paymentEnabled,
  quotaBalance,
}: {
  userId: string;
  paymentEnabled: boolean | null;
  quotaBalance: AutoDormCheckQuotaBalance;
}) {
  const [taskPage, setTaskPage] = useState(1);
  const [checkInPage, setCheckInPage] = useState(1);
  const tasks = useResource(
    () =>
      api.autoDormCheckTasks(userId, {
        page: taskPage,
        pageSize: DETAIL_PAGE_SIZE,
      }),
    [userId, taskPage],
  );
  const checkIns = useResource(
    () =>
      api.autoDormCheckIns(userId, {
        page: checkInPage,
        pageSize: DETAIL_PAGE_SIZE,
      }),
    [userId, checkInPage],
  );

  return (
    <div className="grid gap-5 border-t border-sand-light bg-cream/35 p-5 lg:grid-cols-2 sm:p-6">
      <HistoryPanel title="任务记录" icon={<Clock3 size={14} />}>
        {tasks.error ? (
          <ErrorState
            error={tasks.error}
            onRetry={() => void tasks.refresh()}
          />
        ) : tasks.loading && !tasks.data ? (
          <LoadingState rows={5} />
        ) : tasks.data?.items.length ? (
          <>
            <div className="divide-y divide-sand-light/70">
              {tasks.data.items.map((task) => (
                <TaskSummary
                  key={task.id}
                  task={task}
                  paymentEnabled={paymentEnabled}
                  quotaBalance={quotaBalance}
                />
              ))}
            </div>
            <Pagination
              page={tasks.data.pagination.page}
              pages={tasks.data.pagination.totalPages}
              total={tasks.data.pagination.total}
              onChange={setTaskPage}
              className="border-t border-sand-light/70 px-4 py-3"
            />
          </>
        ) : (
          <EmptyState title="暂无任务" description="任务生成后会显示在这里。" />
        )}
      </HistoryPanel>

      <HistoryPanel title="打卡历史" icon={<MapPin size={14} />}>
        {checkIns.error ? (
          <ErrorState
            error={checkIns.error}
            onRetry={() => void checkIns.refresh()}
          />
        ) : checkIns.loading && !checkIns.data ? (
          <LoadingState rows={5} />
        ) : checkIns.data?.items.length ? (
          <>
            <div className="divide-y divide-sand-light/70">
              {checkIns.data.items.map((checkIn) => (
                <CheckInSummary key={checkIn.id} checkIn={checkIn} />
              ))}
            </div>
            <Pagination
              page={checkIns.data.pagination.page}
              pages={checkIns.data.pagination.totalPages}
              total={checkIns.data.pagination.total}
              onChange={setCheckInPage}
              className="border-t border-sand-light/70 px-4 py-3"
            />
          </>
        ) : (
          <EmptyState
            title="暂无打卡记录"
            description="成功打卡并记录位置后会显示在这里。"
          />
        )}
      </HistoryPanel>
    </div>
  );
}

function HistoryPanel({
  title,
  icon,
  children,
}: {
  title: string;
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-xl border border-sand-light/80 bg-paper">
      <header className="flex items-center gap-2 border-b border-sand-light/70 px-4 py-3 text-[12.5px] font-medium text-ink">
        <span className="text-clay">{icon}</span>
        {title}
      </header>
      {children}
    </section>
  );
}

function TaskSummary({
  task,
  compact = false,
  paymentEnabled = null,
  quotaBalance = {
    remainingSeconds: 0,
    remainingDays: 0,
    remainingUses: 0,
  },
}: {
  task: AutoDormCheckTask;
  compact?: boolean;
  paymentEnabled?: boolean | null;
  quotaBalance?: AutoDormCheckQuotaBalance;
}) {
  const status = autoDormCheckTaskPresentation(
    task,
    paymentEnabled,
    quotaBalance,
  );
  return (
    <div className={compact ? "flex items-center gap-2" : "px-4 py-3"}>
      <div
        className={
          compact ? "min-w-0 flex-1" : "flex items-center justify-between gap-3"
        }
      >
        <p className="truncate text-[11.5px] text-ink-soft">
          {formatTaskTime(task)}
        </p>
        <span className="flex flex-none items-center gap-1.5">
          {!compact ? (
            <Tag tone={task.executionOrigin === "admin" ? "clay" : "neutral"}>
              {task.executionOrigin === "admin" ? "管理员" : "自动"}
            </Tag>
          ) : null}
          <Tag tone={status.tone}>{status.label}</Tag>
        </span>
      </div>
      {!compact && task.failureMessage ? (
        <p className="mt-1 text-[11px] text-destructive">
          {task.failureMessage}
          {task.failureCode ? ` · ${task.failureCode}` : ""}
        </p>
      ) : null}
    </div>
  );
}

function CheckInSummary({ checkIn }: { checkIn: AutoDormCheckIn }) {
  return (
    <div className="px-4 py-3">
      <div className="flex items-start gap-3">
        <CircleCheckBig size={15} className="mt-0.5 shrink-0 text-sage" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-[12.5px] font-medium text-ink">
            {checkIn.locationName}
          </p>
          <p className="mt-0.5 text-[11px] text-ink-faint">
            {formatDateTime(checkIn.checkedAt)}
          </p>
          <p className="mt-1 font-mono text-[10.5px] text-ink-faint">
            {locationDetail(checkIn)} · 任务 #{checkIn.taskId}
          </p>
        </div>
      </div>
    </div>
  );
}

function locationDetail(checkIn: AutoDormCheckIn) {
  if (checkIn.latitude === null || checkIn.longitude === null) {
    return checkIn.accuracyMeters === null
      ? "未记录坐标"
      : `精度 ${checkIn.accuracyMeters.toFixed(1)} m`;
  }
  const coordinates = `${checkIn.latitude.toFixed(6)}, ${checkIn.longitude.toFixed(6)}`;
  return checkIn.accuracyMeters === null
    ? coordinates
    : `${coordinates} · 精度 ${checkIn.accuracyMeters.toFixed(1)} m`;
}
